import React, { useEffect, useState } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { RouteComponentProps } from "@reach/router";
import messages from "./PageCoachLibrary.intl";
import Content from "../content";
import { Theme, Paper, Box } from "@material-ui/core";
import Exercises from "../exercises-collapse";
import Trainings from "../trainings";
import Button from "../button";
import Link from "../link";
import queryString from "query-string";
import SnackBar from "../snackbar";
import CancelIcon from "@material-ui/icons/CancelOutlined";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import EventNoteIcon from "@material-ui/icons/EventNote";
import SettingsIcon from "@material-ui/icons/Settings";
import Typography from "../typography";
import { IStyledArguments } from "../../types/styled-arguments";

const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  minHeight: "100vh",
  background: theme.palette.extra.backgroundGrey,
  paddingBottom: theme.spacing(10),
}));

// Header Section
const HeaderSection = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const HeaderContent = styled(Box)(({ theme }: IStyledArguments) => ({
  maxWidth: 900,
  margin: "0 auto",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 56,
  height: 56,
  borderRadius: "16px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.8rem",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    width: 48,
    height: 48,
    "& svg": {
      fontSize: "1.5rem",
    },
  },
}));

const HeaderTitle = styled(Typography.H4)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.8rem",
  letterSpacing: "-0.5px",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.4rem",
  },
}));

const TabContainer = styled("div")(({ theme }: IStyledArguments) => ({
  position: "relative",
  paddingBottom: theme.spacing(8),
  // add top padding equal to the sticky tabs offset so content isn't hidden
  paddingTop: 56,
}));

enum TabNames {
  drills = "drills",
  trainings = "trainings",
  admin = "admin",
}
const PATH_TO_TAB: { [path: string]: string } = {
  "/library/drills": TabNames.drills,
  "/library/exercises": TabNames.drills, // Keep for backwards compatibility
  "/library/trainings": TabNames.trainings,
  "/library/admin": TabNames.admin,
};
const TAB_TO_PATH: { [tabName: string]: string } = {
  [TabNames.drills]: "/library/drills",
  [TabNames.trainings]: "/library/trainings",
  [TabNames.admin]: "/library/admin",
};

const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  maxWidth: 900,
  borderRadius: "18px",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
  [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
    borderRadius: 0,
    boxShadow: "none",
    background: "transparent",
    border: "none",
  },
}));

const TabsWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  position: "sticky",
  top: 56,
  zIndex: 10,
  padding: theme.spacing(0.5, 1),
  "& .MuiTabs-indicator": {
    height: 0,
  },
  "& .MuiTabs-flexContainer": {
    gap: theme.spacing(1),
    justifyContent: "center",
  },
  [theme.breakpoints.down("sm")]: {
    top: 56,
    padding: theme.spacing(0.5),
  },
}));

// Custom Tab styling with icons
const StyledTab = styled(Tab)(({ theme }: IStyledArguments) => ({
  minHeight: 52,
  minWidth: 120,
  borderRadius: "14px",
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  fontSize: "0.85rem",
  textTransform: "none",
  color: theme.palette.text.secondary,
  background: "transparent",
  border: "2px solid transparent",
  transition: "all 0.2s ease",
  "& .MuiTab-wrapper": {
    flexDirection: "row",
    gap: theme.spacing(1),
    "& svg": {
      marginBottom: "0 !important",
      fontSize: "1.2rem",
    },
  },
  "&:hover": {
    background: theme.palette.extra.accentSoft,
    color: theme.palette.primary.main,
  },
  "&.Mui-selected": {
    background: theme.palette.extra.accentSoft,
    color: theme.palette.primary.dark,
    border: "2px solid rgba(14, 124, 134, 0.3)",
    fontWeight: 700,
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  "&.Mui-disabled": {
    opacity: 0.4,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 90,
    padding: theme.spacing(0.75, 1.5),
    fontSize: "0.75rem",
    "& .MuiTab-wrapper svg": {
      fontSize: "1rem",
    },
  },
}));

type PagePeopleProps = Required<
  Pick<RouteComponentProps, "navigate" | "path" | "location">
>;

const PageCoachLibrary = injectIntl<PagePeopleProps>(
  ({ intl, navigate, path, location }) => {
    let params = queryString.parse(location.search);
    let action = params["action"];

    const [openSnackbar, setOpenSnackbar] = useState<boolean>(
      action === "trainingDeleted" || action === "drillDeleted" || action === "exerciseDeleted"
    );
    const currentTab = PATH_TO_TAB[path] || TabNames.drills;

    const handleChange = (_: React.ChangeEvent<{}>, newValue: number) => {
      navigate(TAB_TO_PATH[newValue], {
        replace: true,
      });
    };

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [currentTab]);

    return (
      <PageContainer>
        <HeaderSection>
          <HeaderContent>
            <HeaderIcon>
              <LibraryBooksIcon />
            </HeaderIcon>
            <HeaderTitle>Coach Library</HeaderTitle>
            <div style={{ flex: 1 }} />
            <Link to="/library/drills/ai-generator" style={{ textDecoration: "none" }}>
              <Button color="primary">Generate with AI</Button>
            </Link>
          </HeaderContent>
        </HeaderSection>
        
        <Content>
          <StyledPaper elevation={0}>
            <TabsWrapper>
              <Tabs
                centered
                value={currentTab}
                onChange={handleChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                scrollButtons="auto"
              >
                <StyledTab
                  icon={<FitnessCenterIcon />}
                  label={intl.formatMessage(messages.drills)}
                  data-testid="drills-tab"
                  value={TabNames.drills}
                />
                <StyledTab
                  icon={<EventNoteIcon />}
                  label={intl.formatMessage(messages.trainings)}
                  data-testid="trainings-tab"
                  value={TabNames.trainings}
                />
                <StyledTab
                  icon={<SettingsIcon />}
                  label={intl.formatMessage(messages.admin)}
                  data-testid="admin-tab"
                  value={TabNames.admin}
                  disabled
                />
              </Tabs>
            </TabsWrapper>
            <TabContainer>
              {currentTab === TabNames.drills && <Exercises />}
              {currentTab === TabNames.trainings && <Trainings />}
              {currentTab === TabNames.admin && <></>}
            </TabContainer>
          </StyledPaper>
        </Content>
        
        <SnackBar
          data-testid="delete-error"
          open={openSnackbar}
          message={
            action === "trainingDeleted"
              ? intl.formatMessage(messages.trainingDeleted)
              : intl.formatMessage(messages.drillDeleted)
          }
          variant="success"
          action={() => {
            setOpenSnackbar(false);
          }}
          actionText={<CancelIcon />}
        />
      </PageContainer>
    );
  }
);

export default PageCoachLibrary;
